import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api.js';
import { keys } from '../lib/query-client.js';
import { DEFAULT_TIMEZONE } from '../lib/time.js';
import type { SessionUser } from './types.js';

/**
 * Who is signed in (PLAN §15: one owner, no sign-up route).
 *
 * A 401 here is not an error to show — it is simply "nobody is signed in", so
 * it resolves to null and lets the router send the browser to /login.
 */
export function useSession(): UseQueryResult<SessionUser | null> {
  return useQuery({
    queryKey: keys.session,
    retry: false,
    staleTime: 5 * 60_000,
    async queryFn(): Promise<SessionUser | null> {
      try {
        const result = await api.get<{ user: SessionUser }>('/auth/me');
        return result.user;
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthorized) return null;
        throw error;
      }
    },
  });
}

/** The owner's timezone, with a sensible fallback before the session loads. */
export function useTimezone(): string {
  const session = useSession();
  return session.data?.timezone ?? DEFAULT_TIMEZONE;
}

export function useLogin() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api.post<{ user: SessionUser }>('/auth/login', input),
    onSuccess(result) {
      client.setQueryData(keys.session, result.user);
    },
  });
}

export function useLogout() {
  const client = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<void>('/auth/logout'),
    onSuccess() {
      // Order matters, and `clear()` must not be used here.
      //
      // `clear()` removes every query from the cache, including this one —
      // but the mounted `useSession` observer stays attached to the query
      // object that was just removed. The `setQueryData` below then builds a
      // *new* query for the same key, which nothing is observing, so the
      // router never learns the session ended. Signing out appeared to do
      // nothing at all until the page was reloaded, which remounts the
      // observer onto the new query.
      //
      // So: write the fact that nobody is signed in first, while the observer
      // is still listening. That write is what moves the owner to /login.
      client.setQueryData(keys.session, null);

      // Then drop everything else. Removing rather than invalidating means
      // none of the previous owner's data can flash on screen behind the next
      // login — but the session key is spared, because it now holds the
      // answer the router is waiting for.
      client.removeQueries({
        predicate: (query) => query.queryKey[0] !== keys.session[0],
      });
    },
  });
}
